import { Card, Stack, CircularProgress, Typography, Chip } from '@mui/material';
import { useEffect, useState } from 'react';
import type { Deal } from '../../api/services/deals';

interface DealSummaryProps {
  deal: Deal;
  requested: number;
  responded: number;
}

const formatDate = (d: Date) =>
  d.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    day: 'numeric',
  });

export const timeLeft = (until: Date, now = new Date()): string => {
  const diff = until.getTime() - now.getTime();
  if (diff <= 0) return 'Expired';
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  return `${days}d ${hours}h ${minutes}m`;
};

const DealSummary = ({ deal, requested, responded }: DealSummaryProps) => {
  const sealedUntil = new Date(deal.initialWindowEnd);
  const expires = new Date(deal.offerExpDate);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const sealed = now < sealedUntil;
  const expired = now >= expires;
  const accepted = deal.status === 'ACCEPTED';

  return (
    <Card sx={{ p: 4 }}>
      <Stack spacing={2} alignItems="center" textAlign="center">
        {accepted ? (
          <Chip
            color="success"
            label="Offer accepted — the dealer has your contact info"
          />
        ) : expired ? (
          <Chip color="default" label="Offer window closed" />
        ) : sealed ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={28} />
            <Typography variant="h5">Collecting sealed offers</Typography>
          </Stack>
        ) : (
          <Typography variant="h5">Dealers are competing</Typography>
        )}

        <Typography variant="body1" color="text.secondary">
          {requested} dealer{requested === 1 ? '' : 's'} asked · {responded}{' '}
          responded
        </Typography>

        {!accepted && !expired && (
          <>
            {sealed && (
              <Typography variant="body2">
                Until {formatDate(sealedUntil)}, dealers submit blind. After
                that they can see the competing offers and improve theirs.
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Offers close {formatDate(expires)} · {timeLeft(expires, now)} left
            </Typography>
          </>
        )}
      </Stack>
    </Card>
  );
};

export default DealSummary;
